interface PurchaseResultWithIntake {
  pharmacy_order_id: string;
  intake_required: boolean;
}

interface IntakeQueueItem {
  awaiting_order_id: string;
  intake_submitted_at: string | null;
}

/**
 * Route a completed marketplace purchase directly into its product intakes.
 * The backend returns only after every order row is persisted, so these IDs
 * are authoritative and avoid waiting for Home to discover the new tasks.
 */
export function postPurchaseDestination(
  items: readonly PurchaseResultWithIntake[],
): string {
  const orderIds = items
    .filter((item) => item.intake_required)
    .map((item) => item.pharmacy_order_id)
    .filter(Boolean);

  if (orderIds.length === 0) return '/app';

  const params = new URLSearchParams({
    source: 'marketplace_purchase',
    orders: orderIds.join(','),
  });
  return `/app/intake?${params.toString()}`;
}

/**
 * A just-invalidated React Query can synchronously expose its stale cached
 * value before the refetch finishes. Shop checkout knows the exact order IDs
 * that need forms, so do not snapshot/start the queue until all of them are in
 * the response. Already-submitted rows also satisfy this readiness check; a
 * bookmarked purchase link must not wait forever after intake completion.
 */
export function hasEveryPurchasedIntake(
  intakes: readonly IntakeQueueItem[],
  orderIds: readonly string[],
): boolean {
  const knownIds = new Set(intakes.map((intake) => intake.awaiting_order_id));
  return orderIds.every((orderId) => knownIds.has(orderId));
}

/**
 * Keep all outstanding intakes available, but put the just-purchased orders
 * first in the same order the purchase API returned them. Existing recovery
 * links can pass a single ID through the same helper.
 */
export function prioritizePendingIntakes<T extends IntakeQueueItem>(
  intakes: readonly T[],
  priorityOrderIds: readonly string[],
): T[] {
  const pending = intakes.filter((intake) => !intake.intake_submitted_at);
  if (priorityOrderIds.length === 0) return pending;

  const byId = new Map(
    pending.map((intake) => [intake.awaiting_order_id, intake]),
  );
  const prioritized: T[] = [];
  const included = new Set<string>();

  for (const orderId of priorityOrderIds) {
    if (included.has(orderId)) continue;
    const intake = byId.get(orderId);
    if (!intake) continue;
    prioritized.push(intake);
    included.add(orderId);
  }

  return [
    ...prioritized,
    ...pending.filter((intake) => !included.has(intake.awaiting_order_id)),
  ];
}
